import { useMemo } from 'react';
import { Box, Card, CardContent, Chip, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';

import CustomDataGrid from '../../ui/customDataGrid/customDataGrid.component';
import EntityReferenceLink from '../../ui/entityReference/entityReferenceLink.component';
import RelevantDateCell from '../relevantDateCell/relevantDateCell.component';
import { JOB_STATUS_COLOR_MAP } from '../../../consts';
import { getContrastColor } from '../../../utils';

interface ChipListItem {
  title?: string;
  color?: string | null;
  code?: string | null;
}

const ChipsCell = ({ items }: { items: ChipListItem[] }) => {
  if (!items || items.length === 0) return <>-</>;
  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 0.5,
        alignItems: 'center',
        width: '100%',
        height: '100%',
        minHeight: 52,
      }}
    >
      {items.map((item, index) => {
        const title = item.title || '-';
        const label = item.code ? `${title} (${item.code})` : title;
        return (
          <Chip
            key={index}
            label={label}
            size="small"
            sx={
              item.color
                ? {
                    bgcolor: item.color,
                    color: getContrastColor(item.color),
                    '& .MuiChip-label': { color: getContrastColor(item.color) },
                  }
                : {
                    bgcolor: 'rgba(128, 128, 128, 0.25)',
                    color: 'text.secondary',
                  }
            }
          />
        );
      })}
    </Box>
  );
};

interface JobsBreakdownGridProps {
  title: string;
  url: string;
  extraParams: Record<string, any>;
  scopeKey: string;
  canViewJobs: boolean;
  onRowClick?: (id: string) => void;
  // Omit when the table is already scoped to a single technician - every row
  // would repeat the same name.
  showAssignedTo?: boolean;
}

export default function JobsBreakdownGrid({
  title,
  url,
  extraParams,
  scopeKey,
  canViewJobs,
  onRowClick,
  showAssignedTo = true,
}: JobsBreakdownGridProps) {
  const { t } = useTranslation();

  const columns: GridColDef[] = useMemo(() => {
    const cols: GridColDef[] = [
      {
        field: 'number',
        headerName: t('Number'),
        flex: 1,
        minWidth: 140,
        renderCell: (params) => (
          <EntityReferenceLink
            entityType="job"
            entityId={params.row.id}
            label={params.row.number}
            canPreview={canViewJobs}
          />
        ),
      },
      {
        field: 'status',
        headerName: t('Status'),
        width: 150,
        renderCell: (params) => {
          const status = params.row.status;
          const label = params.row.status_display || params.value || '-';
          return (
            <Chip
              label={label}
              color={JOB_STATUS_COLOR_MAP[status] ?? 'default'}
              size="small"
            />
          );
        },
      },
      {
        field: 'timeline_date',
        headerName: t('Relevant date'),
        width: 160,
        sortable: false,
        renderCell: (params) => (
          <RelevantDateCell value={params.value} status={params.row.status} />
        ),
      },
      {
        field: 'list_issues',
        headerName: t('Issues'),
        flex: 1,
        minWidth: 160,
        sortable: false,
        renderCell: (params) => <ChipsCell items={params.row.list_issues || []} />,
      },
      {
        field: 'list_services',
        headerName: t('Services'),
        flex: 1,
        minWidth: 160,
        sortable: false,
        renderCell: (params) => <ChipsCell items={params.row.list_services || []} />,
      },
    ];

    if (showAssignedTo) {
      cols.push({
        field: 'assigned_to',
        headerName: t('Assigned To'),
        flex: 1,
        minWidth: 160,
        sortable: false,
        valueGetter: (value: string[]) => (value?.length ? value.join(', ') : '-'),
      });
    }

    return cols;
  }, [t, canViewJobs, showAssignedTo]);

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {title}
        </Typography>
        <CustomDataGrid
          key={scopeKey}
          url={url}
          columns={columns}
          extraParams={extraParams}
          onRowClick={onRowClick}
          dataGridProps={{
            autoHeight: true,
            getRowHeight: () => 'auto',
            pageSizeOptions: [5, 10, 25],
            disableColumnFilter: true,
          }}
        />
      </CardContent>
    </Card>
  );
}
